import Swiper from "./swiper-bundle.esm.browser.min.js";

const MOBILE_BREAKPOINT = 769;
const DESKTOP_SLIDER_THRESHOLD = 4;
const LOOP_THRESHOLD = 4;

class GutSliderImagesOnly extends HTMLElement {
  constructor() {
    super();
    this._swiper = null;
    this._resizeTimer = null;
    this._initialized = false;
    this._handleResize = this._handleResize.bind(this);
    this._handleSectionLoad = this._handleSectionLoad.bind(this);
    this._handleSectionUnload = this._handleSectionUnload.bind(this);
  }

  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;
    document.addEventListener("shopify:section:load", this._handleSectionLoad);
    document.addEventListener("shopify:section:unload", this._handleSectionUnload);
    window.addEventListener("resize", this._handleResize, { passive: true });
    this._updateLayout();
  }

  disconnectedCallback() {
    this._initialized = false;
    this._destroySwiper();
    window.removeEventListener("resize", this._handleResize);
    document.removeEventListener("shopify:section:load", this._handleSectionLoad);
    document.removeEventListener("shopify:section:unload", this._handleSectionUnload);
    if (this._resizeTimer) {
      clearTimeout(this._resizeTimer);
      this._resizeTimer = null;
    }
  }

  _isOurSection(event) {
    const section = this.closest(".shopify-section");
    return section && (event.target === section || section.contains(event.target));
  }

  _handleSectionLoad(event) {
    if (!this._isOurSection(event)) return;
    this._destroySwiper();
    this._updateLayout();
  }

  _handleSectionUnload(event) {
    if (!this._isOurSection(event)) return;
    this._destroySwiper();
  }

  _readConfig() {
    const slideCount = parseInt(this.dataset.slideCount || "0", 10);
    return {
      slideCount,
      sectionId: this.dataset.sectionId || "",
      spaceBetween: parseInt(this.dataset.spaceBetween || "20", 10),
      mobileSlidesPerView: parseFloat(this.dataset.slidesPerViewMobile || "1.2"),
      desktopSlidesPerView: parseFloat(this.dataset.slidesPerViewDesktop || "1"),
      showArrows: this.dataset.showArrows === "true",
      showPagination: this.dataset.showPagination === "true",
      enableLoop: this.dataset.enableLoop === "true",
      enableAutoplay: this.dataset.enableAutoplay === "true",
      autoplaySpeed: parseInt(this.dataset.autoplaySpeed || "5000", 10),
      pauseOnHover: this.dataset.pauseOnHover !== "false",
      pauseOnInteraction: this.dataset.pauseOnInteraction === "true",
    };
  }

  _isMobile() {
    return window.innerWidth < MOBILE_BREAKPOINT;
  }

  _shouldUseSwiper(config) {
    if (config.slideCount <= 0) return false;
    if (this._isMobile()) return true;
    if (config.slideCount > DESKTOP_SLIDER_THRESHOLD) return true;
    return config.slideCount > 1;
  }

  _cleanupSwiperClasses() {
    const swiperEl = this.querySelector("[data-swiper]");
    const container = this.querySelector("[data-swiper-container]");
    const slides = this.querySelectorAll("[data-swiper-slide]");

    if (swiperEl) {
      swiperEl.classList.remove("swiper", "gsi__swiper", "swiper-initialized");
    }
    if (container) {
      container.classList.remove("swiper-wrapper");
    }
    slides.forEach((slide) => slide.classList.remove("swiper-slide"));
  }

  _applyStaticMode() {
    this.classList.add("gsi--static");
    this.classList.remove("gsi--swiper");
    this._cleanupSwiperClasses();
  }

  _prepareSwiperStructure() {
    const swiperEl = this.querySelector("[data-swiper]");
    const container = this.querySelector("[data-swiper-container]");
    const slideEls = this.querySelectorAll("[data-swiper-slide]");

    if (!swiperEl || !container || slideEls.length === 0) return null;

    swiperEl.classList.add("swiper", "gsi__swiper");
    container.classList.add("swiper-wrapper");
    slideEls.forEach((slide) => slide.classList.add("swiper-slide"));

    return { slideEls };
  }

  _syncSlideAccessibility(activeIndex) {
    const slides = this.querySelectorAll("[data-swiper-slide]");
    slides.forEach((slide, index) => {
      const isActive = index === activeIndex;
      slide.setAttribute("aria-hidden", isActive ? "false" : "true");
    });
  }

  _syncMobileImageHeight(swiper) {
    if (!swiper?.params?.autoHeight) return;

    swiper.updateAutoHeight();

    this.querySelectorAll(".gsi__image").forEach((img) => {
      if (img.complete) return;
      img.addEventListener(
        "load",
        () => {
          if (!swiper.destroyed) swiper.updateAutoHeight();
        },
        { once: true },
      );
    });
  }

  _initSwiper(config) {
    const structure = this._prepareSwiperStructure();
    if (!structure) return;

    const { slideEls } = structure;
    const isMobile = this._isMobile();
    const slidesPerView = isMobile
      ? config.mobileSlidesPerView
      : config.desktopSlidesPerView;
    const canNavigate = slideEls.length > 1;
    const canLoop =
      config.enableLoop &&
      canNavigate &&
      slideEls.length > LOOP_THRESHOLD &&
      slideEls.length > slidesPerView;

    this.classList.add("gsi--swiper");
    this.classList.remove("gsi--static");

    const swiperConfig = {
      slidesPerView,
      spaceBetween: config.spaceBetween,
      watchOverflow: true,
      grabCursor: canNavigate,
      speed: 350,
      autoHeight: isMobile,
      keyboard: {
        enabled: true,
        onlyInViewport: true,
      },
      a11y: {
        enabled: true,
        prevSlideMessage: "Previous slide",
        nextSlideMessage: "Next slide",
      },
    };

    if (canLoop) {
      swiperConfig.loop = true;
    }

    if (config.showArrows && canNavigate) {
      const nextEl = this.querySelector(`.gsi__nav-next--${config.sectionId}`);
      const prevEl = this.querySelector(`.gsi__nav-prev--${config.sectionId}`);
      if (nextEl && prevEl) {
        swiperConfig.navigation = { nextEl, prevEl };
      }
    }

    if (config.showPagination && canNavigate) {
      const paginationEl = this.querySelector(
        `.gsi__pagination--${config.sectionId}`,
      );
      if (paginationEl) {
        swiperConfig.pagination = {
          el: paginationEl,
          clickable: true,
          renderBullet: (index, className) => {
            const num = index + 1;
            const total = config.slideCount;
            return `<button type="button" class="${className}" aria-label="Go to slide ${num} of ${total}"></button>`;
          },
        };
      }
    }

    if (config.enableAutoplay && canNavigate) {
      swiperConfig.autoplay = {
        delay: config.autoplaySpeed,
        disableOnInteraction: config.pauseOnInteraction,
        pauseOnMouseEnter: config.pauseOnHover,
      };
    }

    const existingOn = swiperConfig.on || {};
    swiperConfig.on = {
      ...existingOn,
      afterInit: (swiper) => {
        existingOn.afterInit?.(swiper);
        this._syncSlideAccessibility(swiper.realIndex ?? swiper.activeIndex);
        this._syncMobileImageHeight(swiper);
      },
      slideChangeTransitionEnd: (swiper) => {
        existingOn.slideChangeTransitionEnd?.(swiper);
        this._syncSlideAccessibility(swiper.realIndex ?? swiper.activeIndex);
        if (swiper.params.autoHeight) swiper.updateAutoHeight();
      },
      resize: (swiper) => {
        existingOn.resize?.(swiper);
        swiper.update();
        if (swiper.params.autoHeight) swiper.updateAutoHeight();
      },
    };

    const swiperEl = this.querySelector("[data-swiper]");
    this._swiper = new Swiper(swiperEl, swiperConfig);
    requestAnimationFrame(() => this._swiper?.update());
  }

  _destroySwiper() {
    if (this._swiper && !this._swiper.destroyed) {
      this._swiper.destroy(true, true);
    }
    this._swiper = null;
    this._cleanupSwiperClasses();
  }

  _updateLayout() {
    const config = this._readConfig();
    const shouldUseSwiper = this._shouldUseSwiper(config);
    const isSwiperActive = Boolean(this._swiper);

    if (!shouldUseSwiper) {
      this._destroySwiper();
      this._applyStaticMode();
      return;
    }

    if (isSwiperActive) {
      this._destroySwiper();
    }

    this._initSwiper(config);
  }

  _handleResize() {
    if (this._resizeTimer) clearTimeout(this._resizeTimer);
    this._resizeTimer = setTimeout(() => {
      const config = this._readConfig();
      const shouldUseSwiper = this._shouldUseSwiper(config);
      const isSwiperActive = Boolean(this._swiper);

      if (shouldUseSwiper !== isSwiperActive) {
        this._updateLayout();
        return;
      }

      if (this._swiper && !this._swiper.destroyed) {
        const isMobile = this._isMobile();
        this._swiper.params.slidesPerView = isMobile
          ? config.mobileSlidesPerView
          : config.desktopSlidesPerView;
        this._swiper.params.autoHeight = isMobile;
        this._swiper.update();
        if (isMobile) this._swiper.updateAutoHeight();
      }
    }, 150);
  }
}

if (!customElements.get("gut-slider-images-only")) {
  customElements.define("gut-slider-images-only", GutSliderImagesOnly);
}
